// STATUS: Kept here for template purposes in the future
// do not need because we can get the original markdown from GCS directly

// Need to use the React-specific entry point to allow generating React hooks
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { prepareRtkQueryHeaders, transformRtkQueryResponse } from '@/store/apis/rtk-query-utils'
import { kOrgBasePath } from '@/constants/constants-api-paths'
import {
  CreateUserInviteRequest,
  CreateUserInviteResponse,
  CreateUserInviteResponseSchema,
  GetOrganizationWithDetailsResponse,
  GetOrganizationWithDetailsResponseSchema,
  ResendInviteResponse,
  ResendInviteResponseSchema,
} from '../schemas'

// Define a service using a base URL and expected endpoints
export const orgAdminApi = createApi({
  reducerPath: 'orgAdminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: kPaxtonAppApiRESTBaseUrl(),
    prepareHeaders: async (headers) => await prepareRtkQueryHeaders(headers),
  }),

  endpoints: (builder) => ({
    //====================================================================================================
    // Get Org Details (Org, Users, Invites)
    listUsers: builder.query<GetOrganizationWithDetailsResponse, void>({
      query: () => ({
        url: `${kOrgBasePath}/details`,
        method: 'GET',
      }),
      keepUnusedDataFor: 5, // short cache, may need fast updates

      // Validate the response
      transformResponse: (response) => {
        const validatedResponse = GetOrganizationWithDetailsResponseSchema.safeParse(response)
        if (!validatedResponse.success) {
          console.error('Error validating list users response:', validatedResponse.error)
          throw new Error(validatedResponse.error.message)
        }
        return validatedResponse.data as GetOrganizationWithDetailsResponse
      },

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Remove user from organization
    removeUserFromOrg: builder.mutation<void, string>({
      query: (userId) => ({
        url: `${kOrgBasePath}/user/${userId}`,
        method: 'DELETE',
      }),

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Role to user
    changeRoleToUser: builder.mutation<void, string>({
      query: (userId) => ({
        url: `${kOrgBasePath}/user/${userId}/role/to-user`,
        method: 'POST',
      }),

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Role to admin
    changeRoleToAdmin: builder.mutation<void, string>({
      query: (userId) => ({
        url: `${kOrgBasePath}/user/${userId}/role/to-admin`,
        method: 'POST',
      }),

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Send Invite
    sendInvite: builder.mutation<CreateUserInviteResponse, CreateUserInviteRequest>({
      query: (args) => ({
        url: `${kOrgBasePath}/invite`,
        method: 'POST',
        body: args,
      }),

      // Validate the response
      transformResponse: (response) => {
        const validatedResponse = CreateUserInviteResponseSchema.safeParse(response)
        if (!validatedResponse.success) {
          console.error('Error validating send invite response:', validatedResponse.error)
          throw new Error(validatedResponse.error.message)
        }
        return validatedResponse.data as CreateUserInviteResponse
      },

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Delete Invite
    deleteInvite: builder.mutation<void, string>({
      query: (inviteId) => ({
        url: `${kOrgBasePath}/invite/${inviteId}`,
        method: 'DELETE',
      }),

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Accept Invite
    acceptInvite: builder.mutation<void, { invite_id: string; brand?: string }>({
      query: ({ invite_id, brand }) => ({
        url: `${kOrgBasePath}/invite/register`,
        method: 'POST',
        body: {
          invite_id,
          ...(brand && { brand }),
        },
      }),

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),

    //====================================================================================================
    // Resend Invite
    resendInvite: builder.mutation<ResendInviteResponse, string>({
      query: (invite_id) => ({
        url: `${kOrgBasePath}/invite/resend`,
        method: 'POST',
        body: {
          invite_id,
        },
      }),

      // Validate the response
      transformResponse: (response) => {
        const validatedResponse = ResendInviteResponseSchema.safeParse(response)
        if (!validatedResponse.success) {
          console.error('Error validating resend invite response:', validatedResponse.error)
          throw new Error(validatedResponse.error.message)
        }
        return validatedResponse.data as ResendInviteResponse
      },

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),
  }),
})

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useListUsersQuery,
  useRemoveUserFromOrgMutation,
  useChangeRoleToAdminMutation,
  useChangeRoleToUserMutation,
  useSendInviteMutation,
  useDeleteInviteMutation,
  useAcceptInviteMutation,
  useResendInviteMutation,
} = orgAdminApi
